import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, RefreshCw, FileText, Download, Settings } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';

export const LoremGeneratorTool: React.FC = () => {
  const { t } = useTranslation();
  const [output, setOutput] = useState('');
  const [type, setType] = useState<'words' | 'sentences' | 'paragraphs'>('paragraphs');
  const [count, setCount] = useState(3);
  const [startWithLorem, setStartWithLorem] = useState(true);
  const [variant, setVariant] = useState<'classic' | 'modern' | 'hipster' | 'tech'>('classic');

  const loremWords = {
    classic: [
      'lorem', 'ipsum', 'dolor', 'sit', 'amet', 'consectetur', 'adipiscing', 'elit',
      'sed', 'do', 'eiusmod', 'tempor', 'incididunt', 'ut', 'labore', 'et', 'dolore',
      'magna', 'aliqua', 'enim', 'ad', 'minim', 'veniam', 'quis', 'nostrud',
      'exercitation', 'ullamco', 'laboris', 'nisi', 'aliquip', 'ex', 'ea', 'commodo',
      'consequat', 'duis', 'aute', 'irure', 'in', 'reprehenderit', 'voluptate',
      'velit', 'esse', 'cillum', 'fugiat', 'nulla', 'pariatur', 'excepteur', 'sint',
      'occaecat', 'cupidatat', 'non', 'proident', 'sunt', 'culpa', 'qui', 'officia',
      'deserunt', 'mollit', 'anim', 'id', 'est', 'laborum', 'at', 'vero', 'eos',
      'accusamus', 'accusantium', 'doloremque', 'laudantium', 'totam', 'rem',
      'aperiam', 'eaque', 'ipsa', 'quae', 'ab', 'illo', 'inventore', 'veritatis',
      'et', 'quasi', 'architecto', 'beatae', 'vitae', 'dicta', 'sunt', 'explicabo'
    ],
    modern: [
      'design', 'user', 'experience', 'interface', 'digital', 'creative', 'innovation',
      'technology', 'solution', 'modern', 'sleek', 'minimal', 'responsive', 'interactive',
      'dynamic', 'engaging', 'intuitive', 'seamless', 'efficient', 'elegant',
      'sophisticated', 'contemporary', 'cutting-edge', 'streamlined', 'optimized',
      'versatile', 'adaptive', 'scalable', 'robust', 'flexible', 'powerful',
      'advanced', 'professional', 'premium', 'quality', 'excellence', 'performance',
      'functionality', 'usability', 'accessibility', 'compatibility', 'reliability'
    ],
    hipster: [
      'artisan', 'craft', 'organic', 'sustainable', 'authentic', 'vintage', 'retro',
      'handmade', 'bespoke', 'curated', 'boutique', 'indie', 'alternative', 'unique',
      'original', 'creative', 'artistic', 'aesthetic', 'minimalist', 'rustic',
      'urban', 'trendy', 'stylish', 'chic', 'cool', 'edgy', 'underground',
      'experimental', 'innovative', 'progressive', 'contemporary', 'modern',
      'sophisticated', 'refined', 'elegant', 'premium', 'exclusive', 'limited',
      'special', 'rare', 'collectible', 'timeless', 'classic', 'iconic'
    ],
    tech: [
      'algorithm', 'data', 'cloud', 'server', 'database', 'framework', 'API',
      'microservice', 'container', 'deployment', 'scalability', 'performance',
      'optimization', 'security', 'encryption', 'authentication', 'authorization',
      'integration', 'automation', 'machine', 'learning', 'artificial', 'intelligence',
      'blockchain', 'cryptocurrency', 'decentralized', 'distributed', 'network',
      'protocol', 'bandwidth', 'latency', 'throughput', 'redundancy', 'failover',
      'monitoring', 'analytics', 'metrics', 'dashboard', 'visualization', 'pipeline',
      'workflow', 'agile', 'devops', 'continuous', 'delivery', 'testing'
    ]
  };

  const generateWords = (wordCount: number): string => {
    const words = loremWords[variant];
    const result: string[] = [];
    
    if (startWithLorem && variant === 'classic') {
      result.push('Lorem', 'ipsum', 'dolor', 'sit', 'amet');
      wordCount -= 5;
    }
    
    for (let i = 0; i < wordCount; i++) {
      const randomWord = words[Math.floor(Math.random() * words.length)];
      result.push(randomWord);
    }
    
    return result.join(' ');
  };

  const generateSentence = (): string => {
    const sentenceLength = Math.floor(Math.random() * 15) + 8; // 8-22 words
    const sentence = generateWords(sentenceLength);
    return sentence.charAt(0).toUpperCase() + sentence.slice(1) + '.';
  };

  const generateParagraph = (): string => {
    const sentenceCount = Math.floor(Math.random() * 5) + 3; // 3-7 sentences
    const sentences: string[] = [];
    
    for (let i = 0; i < sentenceCount; i++) {
      sentences.push(generateSentence());
    }
    
    return sentences.join(' ');
  };

  const generateLorem = () => {
    let result = '';
    
    switch (type) {
      case 'words':
        result = generateWords(count);
        break;
      case 'sentences':
        const sentences: string[] = [];
        for (let i = 0; i < count; i++) {
          sentences.push(generateSentence());
        }
        result = sentences.join(' ');
        break;
      case 'paragraphs':
        const paragraphs: string[] = [];
        for (let i = 0; i < count; i++) {
          paragraphs.push(generateParagraph());
        }
        result = paragraphs.join('\n\n');
        break;
    }
    
    setOutput(result);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopy'));
    }
  };

  const downloadResult = () => {
    if (output) {
      const blob = new Blob([output], { type: 'text/plain' });
      saveAs(blob, `lorem-${type}-${count}.txt`);
    }
  };

  const clearOutput = () => {
    setOutput('');
  };

  // Generate initial content
  React.useEffect(() => {
    generateLorem();
  }, []);

  const presets = [
    { type: 'words' as const, count: 50, label: t('tools.loremGenerator.presets.50Words') },
    { type: 'words' as const, count: 100, label: t('tools.loremGenerator.presets.100Words') },
    { type: 'sentences' as const, count: 5, label: t('tools.loremGenerator.presets.5Sentences') },
    { type: 'sentences' as const, count: 10, label: t('tools.loremGenerator.presets.10Sentences') },
    { type: 'paragraphs' as const, count: 3, label: t('tools.loremGenerator.presets.3Paragraphs') },
    { type: 'paragraphs' as const, count: 5, label: t('tools.loremGenerator.presets.5Paragraphs') }
  ];

  const usePreset = (preset: { type: 'words' | 'sentences' | 'paragraphs'; count: number }) => {
    setType(preset.type);
    setCount(preset.count);
    setTimeout(() => generateLorem(), 100);
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="w-5 h-5" />
            <span>{t('tools.loremGenerator.name')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tools.loremGenerator.generate')}:
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as 'words' | 'sentences' | 'paragraphs')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="words">{t('tools.loremGenerator.words')}</option>
                <option value="sentences">{t('tools.loremGenerator.sentences')}</option>
                <option value="paragraphs">{t('tools.loremGenerator.paragraphs')}</option>
              </select>
            </div>

            {/* Count Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tools.loremGenerator.count')}:
              </label>
              <Input
                type="number"
                min="1"
                max="100"
                value={count}
                onChange={(e) => setCount(parseInt(e.target.value) || 1)}
              />
            </div>

            {/* Variant Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tools.loremGenerator.style')}:
              </label>
              <select
                value={variant}
                onChange={(e) => setVariant(e.target.value as 'classic' | 'modern' | 'hipster' | 'tech')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="classic">{t('tools.loremGenerator.classicLorem')}</option>
                <option value="modern">{t('tools.loremGenerator.modernDesign')}</option>
                <option value="hipster">{t('tools.loremGenerator.hipster')}</option>
                <option value="tech">{t('tools.loremGenerator.techProgramming')}</option>
              </select>
            </div>

            {/* Start with Lorem */}
            <div className="flex items-center">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={startWithLorem}
                  onChange={(e) => setStartWithLorem(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  disabled={variant !== 'classic'}
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {t('tools.loremGenerator.startWithLorem')}
                </span>
              </label>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            <Button
              onClick={generateLorem}
              variant="primary"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {t('common.generate')}
            </Button>
            <Button
              onClick={() => output && copyToClipboard(output)}
              disabled={!output}
              variant="outline"
            >
              <Copy className="w-4 h-4 mr-2" />
              {t('common.copy')}
            </Button>
            <Button
              onClick={downloadResult}
              disabled={!output}
              variant="outline"
            >
              <Download className="w-4 h-4 mr-2" />
              {t('common.download')}
            </Button>
            <Button
              onClick={clearOutput}
              variant="outline"
            >
              {t('common.clear')}
            </Button>
          </div>

          {/* Quick Presets */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('tools.loremGenerator.quickPresets')}:
            </h4>
            <div className="flex flex-wrap gap-2">
              {presets.map((preset, index) => (
                <Button
                  key={index}
                  onClick={() => usePreset(preset)}
                  variant="outline"
                  size="sm"
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Output */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{t('tools.loremGenerator.generatedText')}</span>
            {output && (
              <span className="text-sm text-gray-500">
                {output.split(' ').length} {t('tools.loremGenerator.words')}, {output.length} {t('tools.loremGenerator.characters')}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={output}
            readOnly
            placeholder={t('tools.loremGenerator.placeholder')}
            className="min-h-[400px] font-serif text-base leading-relaxed"
          />
        </CardContent>
      </Card>

      {/* Style Examples */}
      <Card>
        <CardHeader>
          <CardTitle>Style Examples</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">Classic Lorem</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 font-serif">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit...
                </p>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">Modern Design</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Design user experience interface digital creative innovation...
                </p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">Hipster</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Artisan craft organic sustainable authentic vintage retro...
                </p>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">Tech/Programming</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                  Algorithm data cloud server database framework API...
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
